import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { MdApartment, MdPerson, MdPublic } from 'react-icons/md';
import { Button, Modal } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import useSnackbar from '@hooks/useSnackbar';
import { localized } from '../../appRunView';
import PublicLinkSection from './PublicLinkSection';
import { SHARE_ORG, SHARE_PRIVATE, SHARE_PUBLIC, type ShareLevel, useShareLevel } from './useShareLevel';
import { Actions, DialogBody, Explanation, Levels } from './ShareAppDialog.styled';

interface ShareAppDialogProps {
  readonly draft: UserAppDraft;
  readonly open: boolean;
  readonly onClose: () => void;
}

interface LevelOption {
  readonly level: ShareLevel;
  readonly icon: React.ReactNode;
  readonly label: MessageDescriptor;
  readonly explanation: MessageDescriptor;
}

const LEVELS: readonly LevelOption[] = [
  {
    level: SHARE_PRIVATE,
    icon: <MdPerson size={16} aria-hidden="true" />,
    label: { id: 'apps.share.private', defaultMessage: 'Only me' },
    explanation: { id: 'apps.share.private.body', defaultMessage: 'Only you can open and run it.' },
  },
  {
    level: SHARE_ORG,
    icon: <MdApartment size={16} aria-hidden="true" />,
    label: { id: 'apps.share.org', defaultMessage: 'Organisation' },
    explanation: { id: 'apps.share.org.body', defaultMessage: 'Everyone in your organisation can find and run it in Apps.' },
  },
  {
    level: SHARE_PUBLIC,
    icon: <MdPublic size={16} aria-hidden="true" />,
    label: { id: 'apps.share.public', defaultMessage: 'Public' },
    explanation: {
      id: 'apps.share.public.body',
      defaultMessage:
        'Anyone with the link can open the app, its page and its example without an account. Running it with AI needs an account and uses their credits, never yours. Keep anything secret out of its instructions.',
    },
  },
];

const ShareAppDialog: React.FC<ShareAppDialogProps> = ({ draft, open, onClose }) => {
  const intl = useIntl();
  const { showError } = useSnackbar();
  const publicLinks = useFeatureGate(FeatureGate.OS_APP_PUBLIC_LINKS);
  const share = useShareLevel(draft, open && publicLinks);
  // Unknown link state would read as "no link", and Public would then replace a live one.
  const linkUnknown = share.link.isLoading || share.link.isError;
  const levels = publicLinks ? LEVELS : LEVELS.filter(({ level }) => level !== SHARE_PUBLIC);
  const current = levels.find(({ level }) => level === share.level) ?? levels[0];
  const publicBlocked = share.publicProblems.length > 0 && share.level !== SHARE_PUBLIC;
  const failed = (err: unknown) =>
    showError(
      axiosErrorMessage(err, intl.formatMessage({ id: 'apps.share.error', defaultMessage: 'Sharing could not be changed.' })),
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage({ id: 'apps.share.title', defaultMessage: 'Share {app}' }, { app: localized(intl, draft.manifest.name) })}
      dataTestId="share-app"
    >
      <DialogBody>
        <Levels
          role="radiogroup"
          aria-label={intl.formatMessage({ id: 'apps.share.levels', defaultMessage: 'Who can open it' })}
        >
          {levels.map(({ level, icon, label }) => (
            <Button
              key={level}
              role="radio"
              aria-checked={level === share.level}
              variant={level === share.level ? 'filled' : 'outlined'}
              leadingIcon={icon}
              disabled={share.busy || linkUnknown || (level === SHARE_PUBLIC && publicBlocked)}
              onClick={() => share.choose(level).catch(failed)}
              dataTestId={`share-app__${level}`}
            >
              {intl.formatMessage(label)}
            </Button>
          ))}
        </Levels>
        <Explanation data-testid="share-app__explanation">{intl.formatMessage(current.explanation)}</Explanation>
        {share.link.isError && (
          <Actions>
            <span data-testid="share-app__load-error">
              {intl.formatMessage({ id: 'apps.publicLink.loadError', defaultMessage: 'The public link could not be loaded.' })}
            </span>
            <Button variant="outlined" size="sm" onClick={() => void share.link.refetch()} dataTestId="share-app__retry">
              {intl.formatMessage({ id: 'apps.publicLink.retry', defaultMessage: 'Try again' })}
            </Button>
          </Actions>
        )}
        {publicLinks && (share.level === SHARE_PUBLIC || share.publicProblems.length > 0) && (
          <PublicLinkSection
            state={share.link.data}
            loading={share.link.isLoading}
            busy={share.busy}
            onNewLink={share.newLink}
            onFailed={failed}
          />
        )}
      </DialogBody>
    </Modal>
  );
};

export default ShareAppDialog;
